import { useEffect, useState } from "react";

import { getExternalProductOptions } from "services/menu/rest";
import { ExternalProductOptionsDto } from "services/menu/restModels";

export interface UseExternalProductOptionsResult {
    options: ExternalProductOptionsDto | null;
    loading: boolean;
    error: boolean;
}

/*
* Опции внешнего товара за сессию не меняются (их обновляет только синхронизация меню),
* поэтому кешируем по паре меню + товар: сводку на главной вкладке позиции и списки в
* диалоге набора обслуживает один запрос, а переключение типа набора не ходит в сеть.
* */
const cache: Record<string, ExternalProductOptionsDto> = {};
const inFlight: Record<string, Promise<ExternalProductOptionsDto | null>> = {};

const cacheKey = (menuId: number, externalProductId: string) => `${menuId}|${externalProductId}`;

const loadOptions = (
    menuId: number,
    externalProductId: string
): Promise<ExternalProductOptionsDto | null> => {
    const key = cacheKey(menuId, externalProductId);

    if (cache[key]) return Promise.resolve(cache[key]);

    if (!inFlight[key]) {
        inFlight[key] = getExternalProductOptions(menuId, externalProductId)
            .then((result) => {
                if (result) cache[key] = result;
                return result;
            })
            .finally(() => {
                delete inFlight[key];
            });
    }

    return inFlight[key];
};

export const useExternalProductOptions = (
    menuId: number | null,
    externalProductId: string | null,
    enabled: boolean
): UseExternalProductOptionsResult => {
    const key = menuId !== null && externalProductId !== null ? cacheKey(menuId, externalProductId) : null;

    const [options, setOptions] = useState<ExternalProductOptionsDto | null>(
        key ? cache[key] || null : null
    );
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);

    useEffect(() => {
        if (!enabled || menuId === null || externalProductId === null) return;

        // ignore-флаг: ответ после размонтирования не трогает стейт.
        let ignore = false;

        setLoading(true);
        setError(false);

        loadOptions(menuId, externalProductId)
            .then((result) => {
                if (ignore) return;
                setOptions(result);
                if (!result) setError(true);
            })
            .catch(() => {
                if (ignore) return;
                setOptions(null);
                setError(true);
            })
            .finally(() => {
                if (ignore) return;
                setLoading(false);
            });

        return () => {
            ignore = true;
        };
    }, [enabled, menuId, externalProductId]);

    return { options, loading, error };
};
